import React, { useState, useEffect } from 'react';
import { Bug, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const DebugPanel = () => {
  const { user } = useAuth();
  const [debugEnabled, setDebugEnabled] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const isMainAdmin = user?.role === 'admin' || user?.role === 'main_admin';

  useEffect(() => {
    if (user) {
      checkDebugMode();
    } else {
      setLoading(false);
    }
  }, [user]);

  const checkDebugMode = async () => {
    try {
      const res = await api.get('/settings/debug-mode');
      const enabled = res.data.enabled || false;
      setDebugEnabled(enabled);
      if (enabled) {
        await fetchDebugInfo();
      }
    } catch (error) {
      console.error('Failed to check debug mode');
    } finally {
      setLoading(false);
    }
  };

  const fetchDebugInfo = async () => {
    try {
      const res = await api.get('/settings/debug-info');
      setDebugInfo(res.data);
    } catch (error) {
      console.error('Failed to fetch debug info');
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDebugInfo();
    setRefreshing(false);
  };

  if (loading || !user || !debugEnabled || !debugInfo) {
    return null;
  }

  const { user: userInfo, platform, jwt_claims: jwtClaims } = debugInfo;

  const formatIdWithName = (name, id) => {
    if (!id || id === 'N/A') return 'N/A';
    return name ? `${name} - ${id}` : id;
  };

  const formatBool = (val) => {
    if (val === true) return 'Yes';
    if (val === false) return 'No';
    return String(val ?? 'N/A');
  };

  const userRows = [
    { label: 'User ID', value: userInfo.user_id },
    { label: 'Email', value: userInfo.email },
    { label: 'Name', value: `${userInfo.first_name || ''} ${userInfo.last_name || ''}`.trim() || 'N/A' },
    { label: 'Phone', value: userInfo.phone },
    { label: 'Role', value: userInfo.role },
    { label: 'User Type', value: userInfo.user_type },
    { label: 'Account Type', value: userInfo.account_type },
    { label: 'Company ID', value: formatIdWithName(userInfo.company_name, userInfo.company_id) },
    { label: 'Referred By Company', value: userInfo.referred_by_company_id },
    { label: 'Tier', value: userInfo.tier },
    { label: 'Tier Status', value: userInfo.tier_status },
    { label: 'Email Verified', value: formatBool(userInfo.email_verified) },
    { label: 'Is Active', value: formatBool(userInfo.is_active) },
    { label: 'Approval Status', value: userInfo.approval_status },
    { label: 'Auth Provider', value: userInfo.auth_provider },
    { label: 'Profile Complete', value: formatBool(userInfo.has_completed_profile) },
    { label: 'Setup Complete', value: formatBool(userInfo.has_completed_setup) },
    { label: 'Verification Complete', value: formatBool(userInfo.has_completed_verification) },
    { label: 'Login Count', value: userInfo.login_count },
    { label: 'Last Login At', value: userInfo.last_login_at },
    { label: 'Last Login IP', value: userInfo.last_login_ip },
    { label: 'Referral ID', value: formatIdWithName(userInfo.referred_by_name, userInfo.referred_by) },
    { label: 'Parent ID', value: formatIdWithName(userInfo.parent_name, userInfo.parent_id) },
    { label: 'Created At', value: userInfo.created_at },
    { label: 'Updated At', value: userInfo.updated_at },
  ];

  const jwtRows = jwtClaims ? [
    { label: 'sub', value: jwtClaims.sub },
    { label: 'email', value: jwtClaims.email },
    { label: 'role', value: jwtClaims.role },
    { label: 'company_id', value: jwtClaims.company_id },
    { label: 'referred_by_company_id', value: jwtClaims.referred_by_company_id },
  ] : [];

  const platformRows = platform ? [
    { label: 'Total Users', value: platform.total_users },
    { label: 'Total Companies', value: platform.total_companies },
    { label: 'Total Tickets', value: platform.total_tickets },
    { label: 'Pending Tickets', value: platform.pending_tickets, highlight: platform.pending_tickets > 0 },
    { label: 'Total Quotes', value: platform.total_quotes },
    { label: 'Pending Quotes', value: platform.pending_quotes, highlight: platform.pending_quotes > 0 },
    { label: 'Pending Alerts', value: platform.pending_alerts, highlight: platform.pending_alerts > 0 },
    { label: 'Tier Gold', value: platform.tier_gold },
    { label: 'Tier Silver', value: platform.tier_silver },
    { label: 'Tier Bronze', value: platform.tier_bronze },
    { label: 'Pending Tier Approvals', value: platform.pending_tier_approvals, highlight: platform.pending_tier_approvals > 0 },
    ...(platform.debug_ticket_sample ? [
      { label: '--- Ticket Debug ---', value: '' },
      { label: 'Sample Ticket ID', value: platform.debug_ticket_sample.ticket_id },
      { label: 'Ticket company_id', value: platform.debug_ticket_sample.ticket_company_id },
      { label: 'Ticket referred_by', value: platform.debug_ticket_sample.ticket_referred_by },
      { label: 'Ticket user_id', value: platform.debug_ticket_sample.ticket_user_id },
      { label: 'Query company_id', value: platform.debug_ticket_sample.query_company_id },
    ] : []),
    ...(platform.cdn_loi_info ? [
      { label: '--- CDN / LOI Debug ---', value: '' },
      { label: 'Spaces Enabled', value: formatBool(platform.cdn_loi_info.spaces_enabled) },
      { label: 'CDN Base URL', value: platform.cdn_loi_info.cdn_base_url, link: true },
      { label: 'LOI Folder', value: platform.cdn_loi_info.loi_folder, link: true },
      { label: 'Total LOI Verifications', value: platform.cdn_loi_info.total_loi_verifications },
      { label: 'LOI This Month', value: platform.cdn_loi_info.loi_this_month },
      { label: 'Free LOI Limit/Month', value: platform.cdn_loi_info.free_loi_limit },
      ...(platform.cdn_loi_info.sample_recent_loi ? [
        { label: 'Recent LOI ID', value: platform.cdn_loi_info.sample_recent_loi.id },
        { label: 'Recent LOI Email', value: platform.cdn_loi_info.sample_recent_loi.email },
        { label: 'Recent LOI IP', value: platform.cdn_loi_info.sample_recent_loi.ip },
        { label: 'Recent LOI File', value: platform.cdn_loi_info.sample_recent_loi.filename },
        { label: 'Recent LOI Verified', value: formatBool(platform.cdn_loi_info.sample_recent_loi.email_verified) },
        { label: 'Recent LOI Date', value: platform.cdn_loi_info.sample_recent_loi.created_at },
        { label: 'Recent LOI CDN URL', value: platform.cdn_loi_info.sample_recent_loi.cdn_url, link: true },
      ] : []),
    ] : []),
  ] : [];

  return (
    <div className="mx-4 mt-2 mb-2 sm:mx-6 lg:mx-8">
      <div className="bg-amber-50 border border-amber-200 rounded-xl overflow-hidden shadow-sm">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-amber-100/60 border-b border-amber-200">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-semibold text-amber-800">Debug Mode</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-1 text-amber-600 hover:text-amber-800 transition"
              title="Refresh debug data"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setCollapsed(!collapsed)}
              className="p-1 text-amber-600 hover:text-amber-800 transition"
            >
              {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Content */}
        {!collapsed && (
          <div className="p-4">
            <div className={`grid grid-cols-1 ${platform ? 'lg:grid-cols-3' : 'lg:grid-cols-2'} gap-4`}>
              {/* User Info Table */}
              <div>
                <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">Current User (DB Document)</h4>
                <table className="w-full text-sm">
                  <tbody>
                    {userRows.map((row) => (
                      <tr key={row.label} className="border-b border-amber-100 last:border-0">
                        <td className="py-1.5 pr-3 text-amber-700 font-medium whitespace-nowrap">{row.label}</td>
                        <td className="py-1.5 text-gray-800 font-mono text-xs break-all">{String(row.value ?? 'N/A')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* JWT Claims */}
              {jwtRows.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">JWT Token Claims</h4>
                  <table className="w-full text-sm">
                    <tbody>
                      {jwtRows.map((row) => (
                        <tr key={row.label} className="border-b border-amber-100 last:border-0">
                          <td className="py-1.5 pr-3 text-amber-700 font-medium whitespace-nowrap">{row.label}</td>
                          <td className="py-1.5 text-gray-800 font-mono text-xs break-all">{String(row.value ?? 'N/A')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Platform Stats Table - Main Admin Only */}
              {platform && (
                <div>
                  <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2">Platform Stats</h4>
                  <table className="w-full text-sm">
                    <tbody>
                      {platformRows.map((row) => (
                        <tr key={row.label} className="border-b border-amber-100 last:border-0">
                          <td className="py-1.5 pr-3 text-amber-700 font-medium whitespace-nowrap">{row.label}</td>
                          <td className="py-1.5 font-mono text-xs">
                            {row.link && row.value ? (
                              <a href={String(row.value)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline break-all">
                                {String(row.value)}
                              </a>
                            ) : (
                              <span className={row.highlight ? 'text-red-600 font-semibold' : 'text-gray-800'}>
                                {String(row.value ?? '0')}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DebugPanel;
